import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Captions, Languages, LoaderCircle, Settings2 } from 'lucide-react';
import { useTranslation } from '@/hooks';
import type { Language, MediaLink } from '@/types/model';
import type { PlaybackLanguageParams, PlaybackUrlResponse } from '@/services/tvShowService';
import { getVodTracks, loadVodEngine, selectVodSubtitle, seekVodNative, setVodNativeDuration, type VodEngine, type VodTrack } from './vodEngine';
import { languageCodes, samePlaybackSource } from './vodTracks';

interface VodPlayerProps {
  links?: MediaLink[];
  poster?: string;
  title: string;
  autoPlay?: boolean;
  loadPlaybackUrl: (params: PlaybackLanguageParams) => Promise<PlaybackUrlResponse>;
}

interface PlaybackSession {
  player: VodEngine | null;
  loader: VodPlayerProps['loadPlaybackUrl'];
  response: PlaybackUrlResponse | null;
  pending: Promise<void>;
  disposed: boolean;
  audioLanguage?: Language;
  subtitleLanguage?: Language;
  subtitleEnabled: boolean;
  busy: boolean;
  playing: boolean;
  surface: HTMLDivElement;
}

const emptyLinks: MediaLink[] = [];
const validLanguages = (languages: Language[] = []) => languages.filter((language) => language in languageCodes);
const uniqueLanguages = (links: MediaLink[], key: 'audioLanguages' | 'subtitleLanguages') => (
  Array.from(new Set(links.filter((link) => link.active).flatMap((link) => validLanguages(link[key]))))
);
const formatLanguage = (language: Language, locale: string) => (
  new Intl.DisplayNames([locale], { type: 'language' }).of(languageCodes[language][0]) ?? language
);

const VodPlayer = ({ links, poster, title, autoPlay = true, loadPlaybackUrl }: VodPlayerProps) => {
  const { language: interfaceLanguage, t } = useTranslation();
  const hostRef = useRef<HTMLDivElement>(null);
  const [trackSettingsOpen, setTrackSettingsOpen] = useState(false);
  const sessionRef = useRef<PlaybackSession | null>(null);
  const sourceLinks = links ?? emptyLinks;
  const configuredAudioLanguages = useMemo(() => uniqueLanguages(sourceLinks, 'audioLanguages'), [sourceLinks]);
  const configuredSubtitleLanguages = useMemo(() => uniqueLanguages(sourceLinks, 'subtitleLanguages'), [sourceLinks]);
  const initialAudio = useRef(configuredAudioLanguages[0]);
  const [audioTracks, setAudioTracks] = useState<VodTrack[]>([]);
  const [subtitleTracks, setSubtitleTracks] = useState<VodTrack[]>([]);
  const [audioTrack, setAudioTrack] = useState('');
  const [subtitleTrack, setSubtitleTrack] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [playbackError, setPlaybackError] = useState(false);
  const [trackUnavailable, setTrackUnavailable] = useState<'audio' | 'subtitles' | null>(null);
  const [duration, setDuration] = useState(0);
  const volumeRef = useRef(1);

  const syncTracks = useCallback((session: PlaybackSession) => {
    const { player, response } = session;
    if (!player || !response || session.disposed) return;
    const audio = getVodTracks(player, 'Audio', validLanguages(response.audioLanguages));
    const subtitles = getVodTracks(player, 'Subtitle', validLanguages(response.subtitleLanguages));
    const selectedAudio = audio.find((track) => track.id === Number(player.getSelectedAudioStreamId()));
    const selectedSubtitle = subtitles.find((track) => track.id === Number(player.getSelectedSubtitleStreamId()));
    session.audioLanguage = selectedAudio?.language;
    session.subtitleLanguage = session.subtitleEnabled ? selectedSubtitle?.language : undefined;
    setAudioTracks(audio);
    setSubtitleTracks(subtitles);
    setAudioTrack(selectedAudio ? String(selectedAudio.id) : '');
    setSubtitleTrack(session.subtitleEnabled && selectedSubtitle ? String(selectedSubtitle.id) : '');
    setDuration(Number(player.getDuration()) / 1000);
  }, []);

  const openSource = useCallback(async (session: PlaybackSession, params: PlaybackLanguageParams, startPlaying: boolean) => {
    const response = await session.loader(params);
    if (session.disposed || !session.player) return;
    const player = session.player;
    const sameSource = session.response && samePlaybackSource(session.response.playbackUrl, response.playbackUrl);
    const previousTime = session.response ? player.currentTime : null;
    const previousRate = session.surface.querySelector('video')?.playbackRate ?? player.getPlaybackRate();
    if (!sameSource) {
      await player.load(new URL(response.playbackUrl, window.location.href).href, {
        ext: new URL(response.playbackUrl, window.location.href).pathname.split('.').pop(),
        http: { credentials: 'omit', referrerPolicy: 'no-referrer' },
      });
      if (session.disposed) return;
      await player.play();
      await setVodNativeDuration(player);
    }
    if (session.disposed) return;
    session.response = response;
    const audio = getVodTracks(player, 'Audio', validLanguages(response.audioLanguages));
    const subtitles = getVodTracks(player, 'Subtitle', validLanguages(response.subtitleLanguages));
    const requestedAudio = audio.find((track) => track.language === params.audioLanguage);
    if (requestedAudio && requestedAudio.id !== Number(player.getSelectedAudioStreamId())) {
      await player.selectAudio(requestedAudio.id);
    }
    const requestedSubtitle = subtitles.find((track) => track.language === params.subtitleLanguage);
    session.subtitleEnabled = Boolean(params.subtitleLanguage && params.subtitleLanguage !== 'NONE' && requestedSubtitle);
    await selectVodSubtitle(player, session.subtitleEnabled ? requestedSubtitle!.id : null);
    if (!sameSource && previousTime !== null) await player.seek(previousTime);
    player.setPlaybackRate(previousRate);
    player.setVolume(volumeRef.current);
    if (!startPlaying) await player.pause();
    if (session.disposed) return;
    setTrackUnavailable(params.audioLanguage && !requestedAudio ? 'audio'
      : params.subtitleLanguage && params.subtitleLanguage !== 'NONE' && !requestedSubtitle ? 'subtitles' : null);
    syncTracks(session);
  }, [syncTracks]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const surface = document.createElement('div');
    surface.className = 'h-full w-full';
    host.append(surface);
    const session: PlaybackSession = {
      player: null, loader: loadPlaybackUrl, response: null,
      pending: Promise.resolve(), disposed: false, subtitleEnabled: false,
      busy: true, playing: false, surface,
    };
    sessionRef.current = session;
    setIsLoading(true);
    setPlaybackError(false);
    setTrackUnavailable(null);
    const resize = new ResizeObserver(() => session.player?.resize(surface.clientWidth, surface.clientHeight));
    resize.observe(surface);
    let nativeObserver: MutationObserver | undefined;
    session.pending = (async () => {
      const Player = await loadVodEngine();
      if (session.disposed) return;
      // Worker mode can leave MP4 subtitle timestamps invalid when switching tracks.
      const player = new Player({ container: surface, enableWorker: false, checkUseMSE: () => true });
      session.player = player;
      player.on('played', () => { session.playing = true; });
      player.on('paused', () => { session.playing = false; });
      player.on('ended', () => { session.playing = false; });
      player.on('error', () => { if (!session.disposed) setPlaybackError(true); });
      const bindControls = () => {
        const video = surface.querySelector('video');
        if (!video || video.controls) return;
        video.controls = true;
        video.setAttribute('aria-label', title);
        const operate = (operation: () => Promise<void>) => {
          if (session.busy || session.disposed) return;
          session.busy = true;
          setIsLoading(true);
          session.pending = session.pending.then(operation).catch(() => {
            if (!session.disposed) setPlaybackError(true);
          }).finally(() => {
            session.busy = false;
            if (!session.disposed) setIsLoading(false);
          });
        };
        video.addEventListener('play', () => {
          if (!session.playing) operate(() => player.play());
        });
        video.addEventListener('pause', () => {
          if (session.playing && !video.ended) operate(() => player.pause());
        });
        // Native controls and the demuxer both emit seeking events. Only user
        // input should initiate another demux seek; engine seeks must not loop.
        let seekIntent = false;
        video.addEventListener('pointerdown', () => { seekIntent = true; });
        video.addEventListener('pointermove', (event) => { if (event.buttons) seekIntent = true; });
        video.addEventListener('keydown', () => { seekIntent = true; });
        video.addEventListener('seeking', () => {
          if (!seekIntent) return;
          seekIntent = false;
          const position = BigInt(Math.round(video.currentTime * 1000));
          operate(() => seekVodNative(player, video, position));
        });
        video.addEventListener('volumechange', () => { volumeRef.current = video.volume; });
      };
      const observer = new MutationObserver(bindControls);
      observer.observe(surface, { childList: true });
      nativeObserver = observer;
      await openSource(session, { audioLanguage: initialAudio.current, subtitleLanguage: 'NONE' }, autoPlay);
    })().catch(() => {
      if (!session.disposed) setPlaybackError(true);
    }).finally(() => {
      session.busy = false;
      if (!session.disposed) setIsLoading(false);
    });
    return () => {
      session.disposed = true;
      resize.disconnect();
      nativeObserver?.disconnect();
      surface.remove();
      // The engine's asynchronous operations must finish before it is destroyed.
      void session.pending.then(() => session.player?.destroy()).catch(() => undefined);
      if (sessionRef.current === session) sessionRef.current = null;
    };
  }, [autoPlay, loadPlaybackUrl, openSource, title]);

  const run = (operation: (session: PlaybackSession, player: VodEngine) => Promise<void>) => {
    const session = sessionRef.current;
    if (!session || session.disposed) return;
    session.busy = true;
    setIsLoading(true);
    setTrackUnavailable(null);
    setPlaybackError(false);
    session.pending = session.pending.then(async () => {
      if (session.disposed || !session.player) return;
      await operation(session, session.player);
      syncTracks(session);
    }).catch(() => {
      if (!session.disposed) setPlaybackError(true);
    }).finally(() => {
      session.busy = false;
      if (!session.disposed) setIsLoading(false);
    });
  };

  const changeAudio = (value: string) => run(async (session, player) => {
    if (value.startsWith('language:')) {
      await openSource(session, {
        audioLanguage: value.slice(9) as Language,
        subtitleLanguage: session.subtitleLanguage ?? 'NONE',
      }, session.playing);
    } else {
      await player.selectAudio(Number(value));
    }
  });
  const changeSubtitles = (value: string) => run(async (session, player) => {
    if (value.startsWith('language:')) {
      await openSource(session, {
        audioLanguage: session.audioLanguage,
        subtitleLanguage: value.slice(9) as Language,
      }, session.playing);
    } else {
      await selectVodSubtitle(player, value === '' ? null : Number(value));
      session.subtitleEnabled = value !== '';
    }
  });
  const trackLabel = (track: VodTrack, index: number) => {
    const language = track.language ? formatLanguage(track.language, interfaceLanguage) : '';
    return [language, track.label].filter(Boolean).join(' — ') || `${t.vodPlayer.track} ${index + 1}`;
  };
  const alternateAudio = configuredAudioLanguages.filter((language) => !audioTracks.some((track) => track.language === language));
  const alternateSubtitles = configuredSubtitleLanguages.filter((language) => !subtitleTracks.some((track) => track.language === language));

  return (
    <div className="relative space-y-4">
      <div className="relative aspect-video overflow-hidden rounded-xl bg-black shadow-inner">
        <div ref={hostRef} aria-label={title} className="h-full w-full" />
        {isLoading && (
          <div role="status" className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 text-white">
            {poster && !duration && <img src={poster} alt="" className="absolute inset-0 h-full w-full object-contain opacity-30" />}
            <div className="relative text-center">
              <LoaderCircle className="mx-auto mb-2 h-8 w-8 animate-spin" />
              <p className="text-sm">{t.vodPlayer.loading}</p>
            </div>
          </div>
        )}
      </div>
      {playbackError && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{t.vodPlayer.playbackError}</p>}
      {trackUnavailable && <p role="status" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
        {trackUnavailable === 'audio' ? t.vodPlayer.audioTrackUnavailable : t.vodPlayer.subtitleTrackUnavailable}
      </p>}
      <div className="absolute right-4 top-4 hidden md:block">
        <button type="button" onClick={() => setTrackSettingsOpen((open) => !open)}
          aria-label={t.vodPlayer.trackSettings} aria-expanded={trackSettingsOpen}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-black/60 text-white shadow-lg backdrop-blur-sm">
          <Settings2 className="h-5 w-5" />
        </button>
      </div>
      <div className={`${trackSettingsOpen ? 'md:absolute md:right-4 md:top-16 md:block md:w-64 md:bg-gray-900 md:p-4 md:text-white md:rounded-xl' : 'md:hidden'} grid gap-3 sm:grid-cols-2`}>
        <label className="space-y-1 text-sm font-medium text-gray-700 md:text-white">
          <span className="flex items-center gap-2"><Languages className="h-4 w-4" />{t.vodPlayer.audioLanguage}</span>
          <select aria-label={t.vodPlayer.audioLanguage} value={audioTrack} onChange={(event) => changeAudio(event.target.value)} disabled={isLoading || audioTracks.length + alternateAudio.length === 0}
            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100">
            {!audioTracks.length && <option value="">{t.vodPlayer.notSpecified}</option>}
            {audioTracks.map((track, index) => <option key={track.id} value={track.id}>{trackLabel(track, index)}</option>)}
            {alternateAudio.map((language) => <option key={language} value={`language:${language}`}>{formatLanguage(language, interfaceLanguage)}</option>)}
          </select>
        </label>
        <label className="space-y-1 text-sm font-medium text-gray-700 md:text-white">
          <span className="flex items-center gap-2"><Captions className="h-4 w-4" />{t.vodPlayer.subtitles}</span>
          <select aria-label={t.vodPlayer.subtitles} value={subtitleTrack} onChange={(event) => changeSubtitles(event.target.value)} disabled={isLoading}
            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100">
            <option value="">{t.vodPlayer.off}</option>
            {subtitleTracks.map((track, index) => <option key={track.id} value={track.id}>{trackLabel(track, index)}</option>)}
            {alternateSubtitles.map((language) => <option key={language} value={`language:${language}`}>{formatLanguage(language, interfaceLanguage)}</option>)}
          </select>
        </label>
      </div>
    </div>
  );
};

export default VodPlayer;
